/**
 * @file Main Nav.
 */
import { gsap } from 'gsap';
import CustomEase from 'gsap/CustomEase';
import { MorphSVGPlugin } from 'gsap/MorphSVGPlugin';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import debounce from 'just-debounce';
import Search from "./nav-search"

// attach autocomplete to search
document.querySelectorAll('header input[name="s"]').forEach( searchInput => Search.addAutoComplete(searchInput))


gsap.registerPlugin(MorphSVGPlugin, CustomEase, ScrollToPlugin);

/**
 * Main nav Component.
 */
const vMainNav = () => {
	const header         = document.querySelector('.v-header');
	const mainNav        = document.querySelector('.v-main-nav');
	const topLinks       = mainNav.querySelectorAll('.menu-item-has-children > .v-main-nav__link');
	const navBg          = document.querySelector('.v-header__main-nav-bg');
	const navBgSvg       = navBg.querySelector('.v-header__main-nav-bg-svg');
	const pageBg         = document.querySelector('.v-page-overlay');
	let currentNavHeight = 0;
	let currentNavItem   = null;
	let navChanging      = false;
	let megaLinks;
	let adSeen           = false;

	class MegaMenu {
		constructor(topLink) {
			this.trigger       = topLink;
			this.item          = topLink.parentNode;
			this.isOpen        = false;
			this.secondaryList = this.item.querySelector('.v-main-nav__secondary-list');

			this.init();
		}

		toggleNav(e) {
			e.preventDefault();
			if (!currentNavItem) {
				this.openNav();
			} else if (currentNavItem && currentNavItem !== this.secondaryList) {
				this.changeNav();
			} else {
				this.closeNav();
			}
		}

		openNav() {
			if (navChanging) {
				return;
			}

			navChanging = true;
			this.trigger.classList.add('open');
			header.classList.add('dropdown-open');
			header.classList.add(`dropdown-open--${this.trigger.getAttribute('data-dropdown')}`);
			this.isOpen = true;

			this.secondaryList.classList.add('v-main-nav__secondary-list--visible');
			const newHeight    = this.secondaryList.getBoundingClientRect().height;
			currentNavItem     = this.secondaryList;
			currentNavHeight   = Math.round(newHeight);
			gsap.set(navBg, { height: Math.round(newHeight) });
			navBg.classList.add('visible');
			pageBg.classList.add('open');
			pageBg.setAttribute('aria-hidden', 'false');
			pageBg.setAttribute('tabindex', '0');

			// a11y: update aria-expanded
			if (this.trigger.parentElement.getAttribute('aria-expanded') === 'false') {
				this.trigger.parentElement.setAttribute('aria-expanded', true);
			}

			gsap.timeline({
				defaults:   { duration: 0.4, ease: 'sine.inOut' },
				onComplete: () => {
					this.secondaryList.classList.add('v-main-nav__secondary-list--opened');
					this.secondaryList.removeAttribute('style');
					// navBgSvg.classList.add('open');
					navBgSvg.setAttribute('data-open', true);
					navBgSvg.removeAttribute('style');
					const chatSection = document.querySelector('section.v-cisco-chat--visible-login');
					const logoLink = document.querySelector('.v-header__logo-link');
					logoLink.setAttribute('tabindex', '1');
					chatSection?.setAttribute('aria-hidden', 'false');
					chatSection?.setAttribute('tabindex', '0');
					//chatSection.focus();
					// a11y: give focus to expanded
					this.trigger.nextElementSibling.focus();

					navChanging = false;
				},
			})
				.to(navBgSvg, { y: 0 })
				.to('#main-nav-bg-up', { morphSVG: '#main-nav-bg-down' }, 0)
				.fromTo(this.secondaryList, { x: 0, yPercent: -10, opacity: 0 }, { x: 0, yPercent: 0, opacity: 1 }, '-=.2');

			// set Segmint ad
			//this.setLoginsAd();

		}

		changeNav() {
			if (navChanging) {
				return;
			}
			navChanging = true;

			this.secondaryList.classList.add('v-main-nav__secondary-list--visible');
			const newHeight         = this.secondaryList.getBoundingClientRect().height;
			const prevSecondaryList = currentNavItem;
			const prevClass         = [...megaLinks].filter((ml) => ml.getOpen());
			prevClass[0].setOpen(false);
			this.isOpen             = true;

			header.classList.remove(`dropdown-open--${prevClass[0].trigger.getAttribute('data-dropdown')}`);
			header.classList.add(`dropdown-open--${this.trigger.getAttribute('data-dropdown')}`);

			this.trigger.classList.add('open');
			prevSecondaryList.parentNode.querySelector('.v-main-nav__link').classList.remove('open');

			gsap.set(navBgSvg, { opacity: 1 });
			gsap.set(this.secondaryList, {
				opacity: 0,
				x:       -40,
			});
			this.secondaryList.classList.add('v-main-nav__secondary-list--opened');
			this.secondaryList.classList.add('v-main-nav__secondary-list--closing');
			prevSecondaryList.classList.add('v-main-nav__secondary-list--closing');
			gsap.timeline({
				defaults:   { duration: 0.4 },
				onComplete: () => {
					prevSecondaryList.classList.remove('v-main-nav__secondary-list--closing');
					prevSecondaryList.classList.remove('v-main-nav__secondary-list--opened');
					prevSecondaryList.classList.remove('v-main-nav__secondary-list--visible');
					prevSecondaryList.removeAttribute('style');

					this.secondaryList.classList.remove('v-main-nav__secondary-list--closing');
					this.secondaryList.removeAttribute('style');

					// navBgSvg.classList.add('open');
					navBgSvg.setAttribute('data-open', true);
					navBgSvg.removeAttribute('style');

					gsap.set(navBg, {
						scaleY: 1,
						height: Math.round(newHeight),
					});
					currentNavHeight      = Math.round(newHeight);

					navChanging = false;
				},
			})
				.fromTo(navBg, { scaleY: 1 }, { scaleY: (newHeight / currentNavHeight), ease: CustomEase.create('custom', 'M0,0,C0.173,0,0.326,0.025,0.438,0.078,0.686,0.194,0.714,0.382,0.768,0.744,0.782,0.84,0.852,0.948,1,1') })
				.fromTo(prevSecondaryList, { opacity: 1, x: 0 }, {
					opacity: 0, x: 40, ease: 'power1.in', duration: 0.35,
				}, 0)
				.fromTo(this.secondaryList, { opacity: 0, x: -40 }, { opacity: 1, x: 0, ease: 'power1.out' }, '>.05');
			currentNavItem                     = this.secondaryList;

			// set Segmint ad
			//this.setLoginsAd();
		}

		closeNav() {
			if (navChanging) {
				return;
			}
			navChanging = true;
			this.trigger.classList.remove('open');
			this.isOpen = false;
			pageBg.setAttribute('aria-hidden', 'true');
			pageBg.setAttribute('tabindex', '-1');

			currentNavItem         = null;
			this.secondaryList.classList.add('v-main-nav__secondary-list--closing');
			gsap.set(navBgSvg, { opacity: 1 });
			pageBg.classList.remove('open');

			// a11y: update aria-expanded
			if (this.trigger.parentElement.getAttribute('aria-expanded') === 'true') {
				this.trigger.parentElement.setAttribute('aria-expanded', false);
			}

			gsap.timeline({
				defaults:   { duration: 0.4, ease: 'sine.inOut' },
				onComplete: () => {
					this.secondaryList.classList.remove('v-main-nav__secondary-list--opened');
					this.secondaryList.classList.remove('v-main-nav__secondary-list--closing');
					this.secondaryList.classList.remove('v-main-nav__secondary-list--visible');
					this.secondaryList.removeAttribute('style');
					// navBgSvg.classList.remove('open');
					navBgSvg.setAttribute('data-open', false);
					navBgSvg.removeAttribute('style');
					navBg.classList.remove('visible');
					header.classList.remove('dropdown-open');
					header.classList.remove(`dropdown-open--${this.trigger.getAttribute('data-dropdown')}`);
					const chatSection = document.querySelector('section.v-cisco-chat--visible-login');
					chatSection?.setAttribute('aria-hidden', 'true');
					chatSection?.removeAttribute('tabindex');
					navChanging = false;
				},
			})
				.fromTo(this.secondaryList, {
					x:        0,
					yPercent: 0,
					opacity:  1,
				}, {
					x:        0,
					yPercent: -20,
					opacity:  0,
				})
				.to('#main-nav-bg-up', { morphSVG: '#main-nav-bg-up' }, '<.15')
				.to(navBgSvg, { y: '-100%' }, '<');
		}

		getOpen() {
			return this.isOpen;
		}

		setOpen(v) {
			this.isOpen = !!v;
		}

		/**
		 * Create ad iFrame.
		 */
		// setLoginsAd() {
		// 	// return if seen
		// 	if (adSeen === true) {
		// 		return false;
		// 	}

		// 	if (this.secondaryList.classList.contains('v-main-nav__logins-wrap')) {
		// 		const parent = document.querySelector('.v-main-nav__secondary-item--ad');
		// 		const { src, wrap } = parent.dataset;
		// 		const wrapper = document.createElement('div');
		// 		const frame = document.createElement('iframe');
		// 		const classes = wrap.split(' ');

		// 		// loop classes from cms,
		// 		// add each to wrapper
		// 		[...classes].map((c) => { wrapper.classList.add(c); });

		// 		// add srd from cms
		// 		// to iframe el
		// 		frame.setAttribute('src', src);
		// 		frame.setAttribute('frameborder', 0);
		// 		frame.setAttribute('seamless', 'seamless');

		// 		// insert new elements
		// 		parent.insertAdjacentElement('afterbegin', wrapper);
		// 		wrapper.insertAdjacentElement('beforeend', frame);

		// 		// flag to prevent
		// 		// multiple ads
		// 		adSeen = true;
		// 	}
		// }

		getFocusContainer() {
			return this.item;
		}

		init() {
			this.trigger.addEventListener('click', (e) => this.toggleNav(e));
			this.item.addEventListener('keyup', (e) => {
				const { code } = e;

				switch (code) {
					case 'Escape': // escape
						if (this.item.getAttribute('aria-expanded') === 'true') {
							this.toggleNav(e);
						}
						break;
					default:
						break;
				}
			});

			pageBg.addEventListener('focusout', (e) => {
				setTimeout(() => {
					if (
						header.contains(document.activeElement)
						|| document.activeElement === pageBg
					) return;

					if (document.querySelector('.v-main-nav__secondary-list--opened')) {
						this.secondaryList = document.querySelector('.v-main-nav__secondary-list--opened');
						this.closeNav();
					}
				}, 0);
			});
		}
	}

	megaLinks = [...topLinks].map((tL) => new MegaMenu(tL));

	/**
	 *
	 */
	function handleNavResize() {
		if (currentNavItem && !navChanging) {
			currentNavHeight   = Math.round(currentNavItem.getBoundingClientRect().height);
			gsap.set(navBg, { height: currentNavHeight });
		}
	}
	window.addEventListener('resize', debounce(handleNavResize, 100));

	pageBg.addEventListener('click', () => {
		const openClass = [...megaLinks].filter((ml) => ml.getOpen());
		openClass[0].closeNav();
	});

	const contactBtn = document.querySelectorAll('.v-main-nav__contact-trigger');

	[...contactBtn].forEach((el) => {
		el.addEventListener('click', (e) => {
			if (el.getAttribute('href').indexOf('#') !== 0) {
				return;
			}
			e.preventDefault();
			const openClass = [...megaLinks].filter((ml) => ml.getOpen());

			if (openClass.length) {
				// console.log( 'openClass', openClass.length );
				openClass[0].closeNav();
			}

			const contactForm = document.querySelector(el.getAttribute('href'));

			// using children[0] bc `contactForm`
			// over shoots the form
			gsap.to(window, {
				scrollTo:   contactForm.children[0].getBoundingClientRect().top + window.pageYOffset,
				duration:   0.5,
				ease:       'power3.easeInOut',
				onComplete: () => {
					contactForm.focus();
				},
			});
		});
	});
};
export default vMainNav;
